import { useEffect, useRef } from 'react';
import { abortableFetch } from '../../utils';
import { useFetchLabelsApi } from './api/fetch-labels';
import { useRunQueryApi } from './api/run-query';
import {
  bindFetchToRef,
  useApiState,
} from '../../components/Hooks/useApiState';

const HEADERS = { 'content-type': 'application/json' };

export const useLabelPerformanceApi = (dateRange) => {
  const fetchLabelsApi = useFetchLabelsApi();
  const runQueryApi = useRunQueryApi(dateRange);

  return { fetchLabelsApi, runQueryApi };
};

function fetchSavedReports(payload) {
  return abortableFetch(
    ['api', 'creative-analytics', 'fetch-saved-reports', payload.view_id].join(
      '/'
    ),
    {
      method: 'GET',
      headers: HEADERS,
      credentials: 'include',
    }
  );
}

function createSavedReport(payload) {
  return abortableFetch(
    ['api', 'creative-analytics', 'create-saved-report'].join('/'),
    {
      method: 'POST',
      headers: HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    }
  );
}

function getSavedReport(payload) {
  return abortableFetch(
    ['api', 'creative-analytics', 'get-saved-report', payload.report_id].join(
      '/'
    ),
    {
      method: 'GET',
      headers: HEADERS,
      credentials: 'include',
    }
  );
}

function deleteSavedReport(payload) {
  return abortableFetch(
    ['api', 'creative-analytics', 'delete-saved-report'].join('/'),
    {
      method: 'DELETE',
      headers: HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    }
  );
}

function updateSavedReport(payload) {
  return abortableFetch(
    ['api', 'creative-analytics', 'update-saved-report'].join('/'),
    {
      method: 'PUT',
      headers: HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    }
  );
}

export function useSavedReportsApi() {
  const mounted = useRef(false);
  const withRef = bindFetchToRef(mounted);
  const [fetchState, fetchDispatch] = useApiState(
    (payload) => withRef(fetchSavedReports(payload)).then(({ data }) => data),
    {
      data: null,
      error: null,
      loading: false,
      count: 0,
    }
  );
  const [postState, postDispatch] = useApiState(
    (payload) => withRef(createSavedReport(payload)).then(({ data }) => data),
    {
      data: null,
      error: null,
      loading: false,
      count: 0,
    }
  );
  const [getState, getDispatch] = useApiState(
    (payload) => withRef(getSavedReport(payload)).then(({ data }) => data),
    {
      data: null,
      error: null,
      loading: false,
      count: 0,
    }
  );
  const [deleteState, deleteDispatch] = useApiState(
    (payload) => withRef(deleteSavedReport(payload)).then(({ data }) => data),
    {
      data: null,
      error: null,
      loading: false,
      count: 0,
    }
  );
  const [updateState, updateDispatch] = useApiState(
    (payload) => withRef(updateSavedReport(payload)).then(({ data }) => data),
    {
      data: null,
      error: null,
      loading: false,
      count: 0,
    }
  );

  useEffect(() => {
    mounted.current = true;

    return () => (mounted.current = false);
  }, []);

  return {
    fetch: {
      ...fetchState,
      request: fetchDispatch,
    },
    post: {
      ...postState,
      request: postDispatch,
    },
    get: {
      ...getState,
      request: getDispatch,
    },
    delete: {
      ...deleteState,
      request: deleteDispatch,
    },
    update: {
      ...updateState,
      request: updateDispatch,
    }
  };
}
